import { FC, ReactNode } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { PositiveMenuItem } from './positiveMenuItem'
import { IRouteDefinition, RouteUrl } from 'app/routes'
import { NegativeMenuItem } from './negativeMenuItem'
import { logger } from 'app/logging/Logger'

import BackIcon from '@tuhi/components/assets/icons/navigationMenu/back.svg'

import NewConsultIcon from '@tuhi/components/assets/icons/navigationMenu/newConsult.svg'
import HistoryIcon from '@tuhi/components/assets/icons/navigationMenu/history.svg'
import PatientSearchIcon from '@tuhi/components/assets/icons/navigationMenu/patientSearch.svg'
import FormsIcon from '@tuhi/components/assets/icons/navigationMenu/forms.svg'

import SettingsIcon from '@tuhi/components/assets/icons/navigationMenu/settings.svg'

import AdminIcon from '@tuhi/components/assets/icons/navigationMenu/person.svg'

import SignOutIcon from '@tuhi/components/assets/icons/navigationMenu/signOut.svg'

import { Header1, Text } from '@tuhi/components'
import { useSignOut } from 'app/hooks/useSignOut'
import { useIsAdmin } from 'app/features/admin/useIsAdmin'
import { media } from '@tuhi/components/src/theme/breakpoints'

const Section = styled.div`
    display: flex;
    flex-direction: column;
`

const Title = styled(Header1)<{ $isCollapsed: boolean }>`
    text-align: center;
    margin-bottom: 40px;

    opacity: ${(p) => (p.$isCollapsed ? 0 : 1)};
    width: ${(p) => (p.$isCollapsed ? 0 : 'auto')};

    ${media.tablet} {
        opacity: 0;
        width: 0;
    }
`

const ReturnContainer = styled.button<{ $isCollapsed: boolean }>`
    cursor: ${(p) => (p.$isCollapsed ? 'pointer' : 'auto')};
    position: absolute;
    display: flex;
    align-items: center;
    border: none;
    background: none;
    gap: 8px;
    min-height: 48px;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    padding: 0;
    opacity: ${(p) => (p.$isCollapsed ? 1 : 0)};
`

const Header = styled.div`
    position: relative;
`

const TopSection = styled(Section)`
    flex: 1;
`

const BottomSection = styled(Section)``

const Column = styled.div<{ $isCollapsed: boolean }>`
    display: flex;
    flex-direction: column;
    background-color: ${(p) => p.theme.colors.tertiary.default};
    padding: ${(p) => (p.$isCollapsed ? '32px 24px' : '32px')};
    border-right: ${(p) => `2px solid ${p.theme.colors.tertiary.tertiary50}`};
    height: 100vh;
    /* ACA-140 (Jon round 3): keep the nav rail inside the viewport. Without
       these, a rail taller than the window (short viewport or browser zoom)
       overflows into #root, which is overflow-y:auto — so #root grows a NATIVE
       scrollbar that stacks with the admin content's own (custom) scrollbar,
       i.e. the reported "double scroll bar". box-sizing folds the padding into
       100vh; overflow-y lets the rail scroll itself instead of pushing #root. */
    box-sizing: border-box;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    position: sticky;
    top: 0;

    ${media.tablet} {
        padding: 32px 24px;
    }
`

export interface ISideMenuItem {
    icon: ReactNode
    route: IRouteDefinition
}

interface ISideMenuItems {
    topSection: ISideMenuItem[]
    bottomSection: ISideMenuItem[]
}

export const menuItems: ISideMenuItems = {
    topSection: [
        {
            icon: <NewConsultIcon />,
            route: RouteUrl.NewConsult,
        },
        {
            icon: <HistoryIcon />,
            route: RouteUrl.History,
        },
        {
            icon: <PatientSearchIcon />,
            route: RouteUrl.Patient,
        },
        {
            icon: <FormsIcon />,
            route: RouteUrl.Forms,
        },
    ],
    bottomSection: [
        {
            icon: <SettingsIcon />,
            route: RouteUrl.Settings,
        },
    ],
}

// Rendered only for admin users — gated in <SideMenu /> via useIsAdmin().
export const adminMenuItem: ISideMenuItem = {
    icon: <AdminIcon />,
    route: RouteUrl.Admin,
}

export const SideMenu: FC = () => {
    const location = useLocation()
    const { pathname } = location
    const { consultId, patientId } = useParams()

    const navigate = useNavigate()
    const signOut = useSignOut()
    const isAdmin = useIsAdmin()

    // Admin nav item is gated: only surfaced when the authenticated user is an
    // admin (mirrors the Settings entry, sits above it in the bottom section).
    const bottomSectionItems: ISideMenuItem[] = isAdmin
        ? [adminMenuItem, ...menuItems.bottomSection]
        : menuItems.bottomSection

    const findMenuItem = (items: ISideMenuItem[]) => {
        return items.find((menu) => {
            return (
                menu.route.path == pathname || menu.route.path + '/' == pathname
            )
        })
    }

    const isCollapsed = () => {
        const menu =
            findMenuItem(menuItems.topSection) ??
            findMenuItem(bottomSectionItems)
        return !menu
    }

    const handleReturn = () => {
        const locationState = location.state as { returnTo?: string } | null

        logger.debug('Return button clicked', {
            action: 'side_menu_return_click',
            metadata: {
                pathname,
                hasLocationState: !!locationState,
                returnTo: locationState?.returnTo,
                consultId,
                patientId,
            },
        })

        if (locationState?.returnTo) {
            logger.debug('Using returnTo from navigation state', {
                action: 'side_menu_return_with_state',
                metadata: { returnTo: locationState.returnTo },
            })
            navigate(locationState.returnTo)
            return
        }

        const back = RouteUrl.back(pathname, consultId, patientId)

        if (back) {
            logger.debug('Using route back property', {
                action: 'side_menu_return_with_back',
                metadata: { back },
            })
            navigate(back)
        } else {
            logger.debug('Using browser back', {
                action: 'side_menu_return_browser_back',
            })
            navigate(-1)
        }
    }

    // On tablet, force collapsed via CSS (Title/text hidden via media queries)
    // The isCollapsed logic still drives desktop behavior
    const collapsed = isCollapsed()

    return (
        <Column $isCollapsed={collapsed}>
            <Header>
                <Title $isCollapsed={collapsed}>Tuhi</Title>

                <ReturnContainer
                    $isCollapsed={collapsed}
                    onClick={handleReturn}
                    disabled={!collapsed}
                >
                    <BackIcon />
                    <Text size="xSmall" $weight="paragraph" $underline={true}>
                        Return
                    </Text>
                </ReturnContainer>
            </Header>

            <TopSection>
                {menuItems.topSection.map((menu) => {
                    return (
                        <PositiveMenuItem
                            key={menu.route.name}
                            menu={menu}
                            isSelected={menu.route.match(pathname)}
                            isCollapsed={collapsed}
                        />
                    )
                })}
            </TopSection>

            <BottomSection>
                {bottomSectionItems.map((menu) => {
                    return (
                        <PositiveMenuItem
                            key={menu.route.name}
                            menu={menu}
                            isSelected={menu.route.match(pathname)}
                            isCollapsed={collapsed}
                        />
                    )
                })}

                <NegativeMenuItem
                    key="Sign out"
                    icon={<SignOutIcon />}
                    text="Sign out"
                    onClick={signOut}
                    isCollapsed={collapsed}
                />
            </BottomSection>
        </Column>
    )
}
